import { Capture } from "./generated/docStorage/model/capture";

// Types
export type ClientStreamHistoryCommonProps = {
  captureId: string;
  streamHeight: number;
  streamWidth: number;
  ts: number;
  version: 0.1;
};

export type ClientStreamHistoryDistinctProps =
  | {
      action: "update";
      streamOutKbit: number;
      bufferedKbit: number;
      targetKbit: number;
    }
  | {
      action: "streamEnd";
    }
  | {
      action: "streamStart";
    };

export type ClientStreamHistoryEvent = ClientStreamHistoryCommonProps &
  ClientStreamHistoryDistinctProps;

export type LiveAssetProvider = "yt" | "fb" | "vlive" | "hlslive";

export type Player = ClaimedPlayer | TeamPlayer;

export type RecordingStorage = "local" | "cloud" | "photos";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UnknownModel = { [key: string]: any };

// Function
export function isClaimedPlayer(player: Player): player is ClaimedPlayer {
  return !("claimedPlayerId" in player);
}

// Interfaces
export interface ClaimedPlayer {
  _id: string;
  _v: string;
  collegeCommitName: string;
  collegeCommitStatus: number;
  createdBecause: string;
  createdTs: number;
  description: string;
  dkPlayerUuid?: string;
  dkTsLatestSwing?: number;
  dkTsPaired?: number;
  dkUpdateFlag?: number;
  DNOR_imgUrlT1000: string;
  DNOR_imgUrlT150: string;
  DNOR_teamIdPriority: string[];
  embedNameFirstLevel: number;
  embedNameLastLevel: number;
  equipment: UnknownModel;
  favoriteTeams?: UnknownModel;
  gpa: string;
  handedness: string;
  handednessBatting: string;
  handednessThrowing: string;
  height: string;
  hometown: null | string;
  invitesRemaining: number;
  isEmbedImagePublicWebsite: boolean;
  isEmbedImageVideo: boolean;
  isNameOnPublicWebsite: boolean;
  modifiedTs: number;
  modifiedUser: string;
  nameFirst: string;
  nameHandle: string;
  nameHandleLower: string;
  nameLast: string;
  notifyGuestPhoneNumbers: string[];
  pinnedClips: string[];
  playerHero: UnknownModel;
  positionPrimary: string;
  positionPrimaryBasketball: string;
  positionPrimarySoccer: string;
  positionPrimaryVB: string;
  positionSecondary: string;
  positionSecondaryBasketball: string;
  positionSecondarySoccer: string;
  positionSecondaryVB: string;
  publicPublishLevel: number;
  socialInstagramHandle: string;
  socialInstagramHandleLower: string;
  socialTiktokHandle: string;
  socialTiktokHandleLower: string;
  socialTwitterHandle: string;
  socialTwitterHandleLower: string;
  socialTwitterPublicFollowers: number;
  socialTwitterPublicFollowersTs: number;
  srAuthorizeUserId?: string;
  srProfileUrl?: string;
  srUserId?: string;
  stars: number;
  userIdSelf: string;
  weight: string;
  yearBirth: number;
  yearGraduation: number;
}

export interface ClaimRequest {
  _id: string;
  claimerEmail: string;
  claimerId: string;
  claimerNameFirst: string;
  claimerNameLast: string;
  claimerPhoneNumber: string;
  claimerPhotoUrl: string;
  createdTs: number;
  isAccepted: boolean;
  isVisible: boolean;
  jerseyNum: string;
  livestreamerNotified: boolean;
  nameFirst: string;
  nameLast: string;
  teamId: string;
  teamNameLong: string;
  teamPlayerId: string;
}

export interface Clip {
  clipLocalTz: string;
  clipStartTs: number;
  clipStopTs: number;
  clipType: string;
  isDkData?: boolean;
  fileDownloadUrl: string;
  fileDownloadUrlFull: string;
  game: TeamGame;
  id: string;
  inningNum: number;
  J_claimedPlayerList: string[];
  J_primary: any;
  J_teamGameId: string;
  J_teamPlayerList: string[];
  O_hideFromClaimedPlayerPage: any[];
  O_teamPlayerList: any[];
  playerRole: string;
  playerScorePlayerList: string[];
  refFull: string;
  refPreview: string;
  setNum: any;
  tagType: any;
  teamAScore: any;
  teamHScore: any;
  teamPlayerActionDict: any;
  textDescription: string;
  textDescriptionBrief: string;
  views: number;
}

export interface DnorGame {
  _id: string;
  awayScore: number | null;
  fb: { cdnUrl: string } | null;
  homeScore: number | null;
  scoreOurName: string;
  J_opponentName: string;
  J_tsStart: number;
  result: string;
  scheduleState: number;
  scoreWeAreHome: boolean;
  sportType: string | null;
  teamASetScores: number[] | null;
  teamHSetScores: number[] | null;
  teamWinnerNAH: number | boolean | null;
  yt: { cdnUrl: string } | null;
}

export interface Highlight {
  _id: string;
  category: string;
  createdTs: number;
  fileUrl720: string;
  gameIdList: string[];
  isEnable: boolean;
  titleMain: string;
  views: number;
}

export interface Invite {
  claimedPlayerId: null | string;
  expires: number;
  id: string;
  invitedUserEmail: string;
  invitedUserId: null | string;
  invitedUserNameFirst: string;
  invitedUserNameLast: string;
  invitedUserPhone: string;
  invitedUserPhotoUrl: null | string;
  teamId: string;
  teamRole: null | string;
  userId: string;
}

export interface Libero {
  _id: string;
  claimedPlayerId: string;
  displayName: string;
  DNOR_imgUrlT1000: null | string;
  DNOR_imgUrlT150: null | string;
  embedNameFirstLevel: number;
  embedNameLastLevel: number;
  invitePhoneNumberList: string[];
  isActive: boolean;
  jerseyNum: string;
  mappedScorePlayerIdList: any[];
  nameFirst: string;
  nameLast: string;
  publicPublishLevel: number;
}

export interface LiveAsset {
  cdnPrivacyStatus: number;
  cdnTitle: string;
  cdnUrl: string;
  destName: string;
  J_teamGameId: string;
  liveM3u8Url?: string;
  tsMax: number;
  tsMin: number;
  type: LiveAssetProvider;
}

export interface PhoneAuthCredential {
  providerId: string;
  verificationCode: string;
  verificationId: string;
}

export interface PublishYT {
  _id: string;
  _v: string;
  channelInfo: YTChannelInfo;
  createdBecause: string;
  createdTs: number;
  credsChannelId: string;
  credsChannelName: string;
  credsTsCreated: number;
  credsTsRefreshed: number;
  followerCount: number;
  followerModifiedTs: number;
  humanPublishName: string;
  isAutoComplete: boolean;
  isAutoRename: boolean;
  isAutoStart: boolean;
  isEnable: boolean;
  isNotifyAdmin: boolean;
  isNotifyCommunity: boolean;
  isPublic: boolean;
  isUnlistedOnConclude: boolean;
  modifiedTs: number;
  modifiedUser: string;
  nextDescription: string;
  nextPrivacy: string;
  nextTitle: string;
  rtmpTimeoutToCompleteBroadcast: number;
  teamId: string;
  ytStreamId: string;
  ytStreamIngestUrl: string;
  ytStreamTitle: string;
}

export interface Radar {
  _id: string;
  _v: string;
  acctEmail: string;
  acctPassword: string;
  acctPullerId: string;
  appId: string;
  createdBecause: string;
  createdTs: number;
  ignoreVeloOver: number;
  ignoreVeloUnder: number;
  isEnable: boolean;
  modifiedTs: number;
  modifiedUser: string;
  prUserId: string;
  teamId: string;
  version: number;
}

export interface Recording {
  _id: string;
  devinstId: string;
  localFilename: string;
  recordingEndTs: number;
  recordingStartTs: number;
  storageType: RecordingStorage;
  teamId: string;
  title: string;
}

export interface SQLUser {
  email: string;
  id: string;
  name: string;
  phoneList: string[];
  players: SQLUserPlayer[];
  providers: string[];
  teams: SQLUserTeam[];
  teamsCoached: SQLUserTeam[];
}

export interface SQLUserPlayer {
  handle: string;
  id: string;
  name: string;
}

export interface SQLUserTeam {
  handle: string;
  id: string;
  isArchived: boolean;
  name: string;
  sportType: string;
}

export interface StreamlogQuery {
  displayCategory: string;
  displayName: string;
  uriTemplate: string;
}

export interface Team {
  _id: string;
  attrib_ageLevel: string;
  attrib_ageU: number;
  attrib_season: number;
  attrib_sportType: string;
  colorTeamPrimary: string;
  coverImage?: { urlOrig: string };
  embedNameFirstLevel: number;
  embedNameLastLevel: number;
  image?: { urlT150: string };
  inviteAdminPhoneNumberList: string[];
  invitePhoneNumberList: string[];
  isAge13Plus: boolean;
  isEmbedImagePublicWebsite: boolean;
  isEmbedImageVideo: boolean;
  isNameOnPublicWebsite: boolean;
  isNotifyCommunityTeamPlayerUpdates: boolean;
  muteAutoTrigger?: boolean;
  muteScope?: string;
  nameHandle: string;
  nameHandleLower: string;
  nameLocation: string;
  nameLong: string;
  nameMed: string;
  nameShort: string;
  pin: string;
  publicPublishLevel: number;
  publishCrosspostMode: string;
  scoreTeamIdList: string[];
  socialFacebookUrl?: string;
  socialInstagramHandle?: string;
  socialTwitterHandle?: string;
  tagger_pullerId: string;
  zipCode: string;
}

export interface TeamCapture extends Capture {
  targetTeamName?: string;
}

export interface TeamGame {
  _id: string;
  _note: string;
  _v: string;
  broadcastState: number;
  broadcastTsEnd: number;
  broadcastTsStart: number;
  createdBecause: string;
  createdTs: number;
  date: string;
  DNOR_teamId: string;
  isEndClipgen: boolean;
  isEndNotified: boolean;
  isLiveTestGame: boolean;
  isQuickStartMode?: boolean;
  liberos?: Libero[];
  matchSetScoreTxt?: string;
  modifiedTs: number;
  modifiedUser: string;
  opponent: string;
  overrideGameState: number;
  overrideOpponentName: string;
  scheduleLocation: string;
  scheduleNotes: string;
  scheduleOpponentName: string;
  scheduleState: number;
  scheduleTitle: string;
  scheduleTsEnd: number;
  scheduleTsStart: number;
  scoreDeviceId: string;
  scoreGameId: string;
  scoreGameState: number;
  scoreHomeIsKnown: boolean;
  scoreInningNum: number;
  scoreLocationLat: number;
  scoreLocationLng: number;
  scoreOpponentName: string;
  scoreOurLineup: Libero[];
  scoreOurName: string;
  scoreOurPitcher: unknown;
  scoreRunsPerHalfInning: any[];
  scoresAwayHome: number[];
  scoreTeamIdOpponent: string;
  scoreTeamIdOurs: string;
  scoreTsEnd: number;
  scoreTsStart: number;
  scoreUrl: string;
  scoreWeAreHome: boolean | string;
  shortKey: string;
  sportType: string;
  teamASetScores?: number[];
  teamHSetScores?: number[];
  teamId: string;
  teamWinnerNAH?: number | boolean | null; // THIS IS GROSS
  time: string;
  title: string;
  tournamentName: string;
  vidSummaryState: number;
}

export interface TeamPlayer {
  _id: string;
  _note: string;
  _v: string;
  claimedPlayerId: string;
  claimedTs: number;
  clipCount?: number;
  createdBecause: string;
  createdTs: number;
  dkPlayerUuid?: string;
  DNOR_teamId: string;
  embedNameFirstLevel: number;
  embedNameLastLevel: number;
  enteredPhone?: string;
  externalData: object;
  externalSource: string;
  inviteEmailList: string[];
  invitePhoneNumberList: string[];
  isActive: boolean;
  isEmbedImagePublicWebsite: boolean;
  isEmbedImageVideo: boolean;
  isForgotten: boolean;
  isNameOnPublicWebsite: boolean;
  isPrivacyTethered: boolean;
  jerseyNum: string;
  mappedScorePlayerIdList: string[];
  modifiedTs: number;
  modifiedUser: string;
  name: string;
  nameFirst: string;
  nameLast: string;
  publicPublishLevel: number;
  sent?: boolean;
}

export interface Thumbnail {
  height: number;
  url: string;
  width: number;
}

export interface Toast {
  background: string;
  label: string;
}

export interface User {
  _v?: string;
  _id?: string;
  nameFirst?: string;
  nameLast?: string;
  email?: string;
  phoneList?: Array<null | string>;
  isPendingClaim?: boolean;
  ROLE_TEAM_ADMIN?: string[];
  ROLE_TEAM_COACH?: string[];
  ROLE_CAPTURE_ADMIN?: string[];
  ROLE_CLAIMED_PLAYER_ADMIN?: string[];
  ROLE_TEAM_FOLLOWER?: string[];
  ROLE_TEAM_PLAYER_FOLLOWER?: string[];
  ROLE_CLAIMED_PLAYER_FOLLOWER?: string[];
  zipCode?: string;
  isAge13Plus?: boolean;
  tsTosAccept?: number;
  invitesRemaining?: number;
  referralUserId?: string;
  referralLink?: string;
  isNotifBlockEmailAll?: boolean;
  isNotifBlockEmailMarketing?: boolean;
  isNotifBlockSmsAll?: boolean;
  isNotifBlockSmsMarketing?: boolean;
  isNotifBlockEmailClips?: boolean;
  isNotifBlockSmsClips?: boolean;
  isNotifBlockSmsStream?: boolean;
  isPermittedToCreateTeam?: boolean;
  appNotificationsEnabled?: boolean;
  createdBecause?: string;
  createdTs?: number;
  modifiedUser?: string;
  modifiedTs?: number;
}

export interface VeloEvent {
  ageTxt: string;
  errorList: string[];
  ts: number;
  velocity: string;
}

export interface YTChannelInfo {
  etag: string;
  id: string;
  kind: string;
  snippet: YTChannelSnippet;
}

export interface YTChannelSnippet {
  customUrl: string;
  description: string;
  localized: {
    title: string;
    description: string;
  };
  publishedAt: string;
  title: string;
  thumbnails: {
    default: Thumbnail;
    medium: Thumbnail;
    high: Thumbnail;
  };
}
